import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators'
import { LazyLoadEvent } from 'primeng/api';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NdmsSafeSubscriber } from '../../../../../../projects/shared/core/interfaces/ndms-safe-subscriber';
import { ToastService } from './../../../../../../projects/shared/core/services/toast.service';
import { UserModel } from 'projects/shared/core/app/api/models/user-model';
import { ResponderService } from '../../../../../../projects/rms/app/api/services/responder.service';
import { ResponderModel } from '../../../../../../projects/rms/app/api/models/responder-model';
import { TooltipConstants } from '../../../../../../projects/rms/app/shared/core-rms/tooltip.constants';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { UserPreferencesService } from "projects/shared/core/services/user-preferences.service";

@Component({
  selector: 'ndms-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.css']
})
export class UsersListComponent extends NdmsSafeSubscriber implements OnInit {
  @Input() set model(value: UserModel[]) {
    this.userList = value;
  }

  @Input() set firstItem(value: number) {
    this.first = value;
  }
  @Input() count;
  @Input() isLoading;

  @Output() reload = new EventEmitter<LazyLoadEvent>();
  @Output() delete = new EventEmitter<UserModel>();
  @Output() edit = new EventEmitter<any>();

  private _responders$: BehaviorSubject<ResponderModel[]> = new BehaviorSubject<ResponderModel[]>([]);
  get responders$() {
    return this._responders$.asObservable();
  }
  get responders() {
    return this._responders$.value;
  }

  private _jpatsUsers$: BehaviorSubject<UserModel[]> = new BehaviorSubject<UserModel[]>([]);
  get jpatsUsers$() {
    return this._jpatsUsers$.asObservable();
  }
  get jpatsUsers() {
    return this._jpatsUsers$.value;
  }

  private _userAppList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  get userAppList$() {
    return this._userAppList$.asObservable();
  }
  get userAppList() {
    return this._userAppList$.value;
  }

  userList: any[] = [];
  userName: string = "";
  first: number = 0;
  pageSizeOptions = [10, 25, 50]; //Keep in ascending order if modifying
  pageSize = null;
  tablePrefKey = "ndmsUsersListPageSize";
  latestEvent: LazyLoadEvent;

  editUserDialog: BsModalRef;

  expand: string = this._tooltipConstants.EXPAND_DETAILS;
  collapse: string = this._tooltipConstants.COLLAPSE_DETAILS;

  columns: any[] = [
    { header: "User's Name", field: "fullName" },
    { header: "User ID", field: "userName" },
    { header: "Last Logged In", field: "lastLoggedIn" }
  ];

  constructor(
    private _bsModalService: BsModalService,
    private _responderService: ResponderService,
    private _toastService: ToastService,
    private _userPreferencesService: UserPreferencesService,
    private _tooltipConstants: TooltipConstants,
    @Inject('PROGRAM') public program: string) {
    super();

    this._userPreferencesService.get(this.tablePrefKey).subscribe(data => {
      this.pageSize = +data?.preferenceValue || 10;
      this.onReloadList({ rows: this.pageSize, first: 0 })
    });
  }

  ngOnInit() {
  }

  formatDate(loggedInDate: string): string {
    let dateString: string = "Not recorded";
    let date = new Date(loggedInDate);
    if (date.getFullYear() > 1) {
      dateString = date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }
    return dateString;
  }

  getUserApplications(user): string {
    let apps: string = "";
    user.userApplications.forEach(app => {
      apps += ", " + app.appItemName;
    })
    return apps.slice(2);
  }

  onEditClick(user: any, event?) {
    let activeElement = document.activeElement;
    let pointerType = event ? event.pointerType : '';

    this.editUserDialog = this._bsModalService.show(UserEditComponent, { backdrop: 'static' });
    this.editUserDialog.content.ndmsUser = user;
    this.editUserDialog.content.patchFormValues();

    this._bsModalService.onHide.pipe(first()).subscribe(() => {
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.focus();
        if (pointerType == "mouse") activeElement.blur();
      }
    });

    this.editUserDialog.content.complete.subscribe(data => {
      if (data) {
        this.edit.emit();
      }
      this.editUserDialog.hide();
    });
  }

  onRefresh() {
    this.getResponders(this.userName, "RMS")
      .then(() => this.onReloadList(this.latestEvent));
  }

  onReloadList(event: LazyLoadEvent) {
    if (event.rows != this.pageSize) {
      this._userPreferencesService.set(this.tablePrefKey, event.rows + '');
      this.pageSize = event.rows;
    }

    this.latestEvent = event;
    if (this.pageSize) this.reload.emit(event);
  }

  onUserExpand(event) {
    let rmsUser = false;
    this.userName = event.data.userName;
    event.data.userApplications.forEach(app => {
      switch (app.appItemName) {
        case "RMS":
          rmsUser = true;
          this.getResponders(this.userName, "RMS");
          break;
      }
    });
    if (!rmsUser) {
      this.getResponders(this.userName, "");
    }
  }

  private getResponders(userName: string, appName: string) {
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this._responderService.RespondersByUserName(userName), data => {
        this._responders$.next(data);
        let userAppList: any[] = [];
        this.responders.forEach(responder => {
          let accountStatus: string = "";
          if (responder.user) {
            switch(responder.user.accountStatus) {
              case 0:
                accountStatus = "Pending";
                break;
              case 1:
                accountStatus = "Enabled";
                break;
              default:
                accountStatus = "Disabled";
                break;
            }
          }
          let userApp = {
            application: appName,
            accountStatus: accountStatus,
            firstName: responder.firstName,
            lastName: responder.lastName,
            userName: userName,
            primaryEmail: responder.primaryEmailAddress,
            secondaryEmail: responder.secondaryEmailAddress,
            responderStatus: responder.status ? responder.status.name : "",
            responderId: responder.id,
            isLegacy: responder.legacyPersonKey ? true : false,
            isUser: responder.user ? responder.user.userName === userName : false
          };
          userAppList.push(userApp);
        });
        this._userAppList$.next(userAppList);
        return resolve();
      }, error => {
        this._toastService.showError(error);
        reject();
      });
    });
  }

}
